import resultService from '../../api/result'
import {
  RESULT_SET_LOADING_STATUS,
  RESULT_SET,
  RESULT_SET_LIST,
  RESULT_INCREASE_OFFSET,
  RESULT_CLEAR_ALL,
  RESULT_EXPAND_DETAILS,
  RESULT_SENDING_XML,
  RESULT_SET_OVERDUE,
  RESULT_SORT_LIST,
  RESULT_TOTAL_COUNT,
  RESULT_BREAK_STATUS,
  RESULT_BEFORE_LIST_SENDING_XML,
  RESULT_FILTERS,
  RESULT_STATISTICS,
  RESULT_STATISTICS_LOADING_STATUS,
  RESULT_SET_SELECTED,
  RESULT_SHOW_OFF_ALL_DETAILS,
  RESULT_SET_QUERY,
  RESULT_FORMAT_DATE
} from '../types'
import { getCookie } from '../../utils/auth-utils'
import notify from '../../api/notification'
import analytics from '../../api/analytics'
import strings from '@/strings'
import { sortColumn } from '../../utils/sort-columns'
import Moment from 'moment'
import { groupObjectByKey } from '../../utils/groupObjectByKey'

const state = {
  resultLoadingStatus: 'STATUS_INITIAL',
  results: [],
  resultsPerPatient: [],
  offset: 0,
  profile: getCookie('profile') || null,
  sendingXml: false,
  sendingXmlBeforeList: false,
  resultList: Boolean,
  startDate: null,
  endDate: null,
  exported: null,
  codeOsLis: null,
  codeOsAlvaro: null,
  patientName: null,
  overdue: false,
  integrated: null,
  dateType: null,
  lastReordenationResult: {
    sort: null,
    column: null
  },
  totalResultsCount: 0,
  breakStatusResults: false,
  filters: {
    principalFilter: {},
    consultFilter: {},
    date: {}
  },
  statistics: {
    resultsNewCount: 0,
    resultsOldCount: 0,
    resultsHasManual: false,
    resultsHasIntegrated: false
  },
  resultStatisticsLoadingStatus: 'STATUS_INITIAL',
  selected: [],
  timestamp: {
    start: null,
    end: null,
    hasTime: false
  }
}

const mutations = {
  [RESULT_SET_LOADING_STATUS](state, resultLoadingStatus) {
    state.resultLoadingStatus = resultLoadingStatus
  },
  [RESULT_SET](state, results) {
    state.results = state.results.concat(results)
    state.resultsPerPatient = groupObjectByKey(state.results, 'patientName')
  },
  [RESULT_INCREASE_OFFSET](state) {
    state.offset++
  },
  [RESULT_CLEAR_ALL](state) {
    state.results = []
    state.offset = 0
    state.resultsPerPatient = []
    state.selected = []
  },
  [RESULT_SET_QUERY](state) {
    state.offset = 0
    state.exported = false
    state.integrated = true
  },
  [RESULT_EXPAND_DETAILS](state, patientName) {
    state.resultsPerPatient[patientName].map(item => {
      item.expanded = !item.expanded
      return item
    })
  },
  [RESULT_SET_LIST](state, query) {
    state.results = []
    state.offset = 0
    state.timestamp.start = query.startDate
    state.timestamp.end = query.endDate
    state.timestamp.hasTime = query.hasTime ? JSON.parse(query.hasTime) : false
    state.exported = query.orderBy || query.exported
    state.integrated = query.filterByOrigin || query.integrated
    state.codeOsLis = query.codeOsLis
    state.codeOsAlvaro = query.q
    state.patientName = query.patientName
    state.dateType = query.dateType
  },
  [RESULT_SENDING_XML](state, sendingXml) {
    state.sendingXml = sendingXml
  },
  [RESULT_BEFORE_LIST_SENDING_XML](state, sendingXmlBeforeList) {
    state.sendingXmlBeforeList = sendingXmlBeforeList
  },
  [RESULT_SET_OVERDUE](state, overdue) {
    state.overdue = overdue
  },
  [RESULT_SORT_LIST](state, param) {
    state.lastReordenationResult = param
    let temporaryArray = state.results
    state.results = []

    if (param.sort === 'asc') {
      state.results = temporaryArray.sort(sortColumn(param.column, param.columnChild, 'one-field-level'))
    } else {
      state.results = temporaryArray.reverse(sortColumn(param.column, param.columnChild, 'one-field-level'))
    }
    state.resultsPerPatient = groupObjectByKey(state.results, 'patientName')
  },
  [RESULT_TOTAL_COUNT](state, count) {
    state.totalResultsCount = count
  },
  [RESULT_BREAK_STATUS](state, value) {
    state.breakStatusResults = value
  },
  [RESULT_FILTERS](state, value) {
    state.filters = value
  },
  [RESULT_STATISTICS](state, value) {
    state.statistics = value
  },
  [RESULT_STATISTICS_LOADING_STATUS](state, value) {
    state.resultStatisticsLoadingStatus = value
  },
  [RESULT_SET_SELECTED](state, value) {
    state.selected = value
  },
  [RESULT_SHOW_OFF_ALL_DETAILS](state, value) {
    state.results = state.results.map(item => {
      item.expanded = value
      return item
    })
  },
  [RESULT_FORMAT_DATE](state) {
    if (state.timestamp.hasTime) {
      state.timestamp.start = Moment(state.timestamp.start).format('YYYYMMDDHHmmss')
      state.timestamp.end = Moment(state.timestamp.end).format('YYYYMMDDHHmmss')
    } else {
      const start = Moment(state.timestamp.start).format('DD/MM/YYYY', true)
      const end = Moment(state.timestamp.end).format('DD/MM/YYYY', true)
      if (!(start === 'Invalid date' || end === 'Invalid date')) {
        state.timestamp.start = Moment(state.timestamp.start).format('DD/MM/YYYY', true)
        state.timestamp.end = Moment(state.timestamp.end).format('DD/MM/YYYY', true)
      }
    }
  }
}

const getters = {
  resultLoadingStatus: state => {
    return state.resultLoadingStatus
  },
  results: state => {
    return state.results
  },
  resultsoffset: state => {
    return state.offset
  },
  hasResults: state => {
    return state.results && state.results.length > 0
  },
  resultList: state => {
    return state.resultList
  },
  resultsLoading: state => {
    return state.resultLoadingStatus === 'STATUS_INITIAL'
  },
  resultPrintingStatus: state => {
    return state.resultPrintingStatus
  },
  showXmlModal: state => {
    return state.sendingXml
  },
  showXmlBeforeListModal: state => {
    return state.sendingXmlBeforeList
  },
  showOverdue: state => {
    return state.overdue
  },
  totalResults: state => {
    return state.totalResultsCount
  },
  resultBreakStatus: state => {
    return state.breakStatusResults
  },
  getResultFilters: state => {
    return state.filters
  },
  getResultStatistics: state => {
    return state.statistics
  },
  getResultStatistictsLoadingStatus: state => {
    return state.resultStatisticsLoadingStatus
  },
  resultsGroupedByNames: state => {
    return state.resultsPerPatient
  },
  resultsSelected: state => {
    return state.selected
  },
  resultHasHourSelected: state => {
    return state.timestamp.hasTime
  }
}

const actions = {
  getServiceResult: function ({ commit, state, rootState }) {
    return resultService
      .getAllResults(
        state.offset,
        strings.listsLimits.results,
        state.samplePrintedList,
        state.timestamp,
        state.exported,
        state.codeOsLis,
        state.codeOsAlvaro,
        state.patientName,
        state.dateType,
        state.integrated,
        rootState.authentication.profile.laboratoryTypeOutputResult
      )
      .then(response => {
        commit(RESULT_BREAK_STATUS, false)
        let results = response.data
        commit(RESULT_SET_OVERDUE, false)
        if (results && results.length > 0) {
          if (
            state.timestamp.start ||
            state.timestamp.end ||
            state.exported ||
            state.q ||
            state.integrated ||
            state.dateType ||
            state.patientName ||
            state.codeOsLis ||
            state.codeOsAlvaro
          ) {
            analyticsOnFetchingFromSearch(state)
          }

          results = results.map(item => {
            item.expanded = false
            return item
          })

          commit(RESULT_SET, results)
          commit(RESULT_INCREASE_OFFSET)
          commit(RESULT_SET_LOADING_STATUS, 'STATUS_SUCCESS')
          if (results.length === strings.listsLimits.results) {
            commit(RESULT_SET_LOADING_STATUS, 'STATUS_SUCCESS')
          } else {
            commit(RESULT_SET_LOADING_STATUS, 'STATUS_END')
          }
          if (state.lastReordenationResult.sort !== null) {
            commit(RESULT_SORT_LIST, state.lastReordenationResult)
          }
        } else {
          commit(RESULT_SET_LOADING_STATUS, 'STATUS_END')
        }
        return response
      })
      .catch(error => {
        if (error.response.data.code === 'IRREGULAR_LABORATORY' || rootState.authentication.profile.defaulter) {
          commit(RESULT_SET_OVERDUE, true)
        }
        commit(RESULT_SET_LOADING_STATUS, 'STATUS_FAILED')
      })
  },
  setWaitingResultLoadingStatus: function ({ commit }) {
    commit(RESULT_SET_LOADING_STATUS, 'STATUS_WAITING')
  },
  clearAllResultData: function ({ commit }) {
    commit(RESULT_SET_LOADING_STATUS, 'STATUS_INITIAL')
    commit(RESULT_CLEAR_ALL)
  },
  selectResultList: function ({ commit }, resultList) {
    commit(RESULT_SET_LOADING_STATUS, 'STATUS_INITIAL')
    commit(RESULT_SET_LIST, resultList)
    commit(RESULT_FORMAT_DATE)
  },
  xmlResults: function ({ commit, rootState }, resultItems) {
    return new Promise((resolve, reject) => {
      if (resultItems.length > 0) {
        commit(RESULT_SENDING_XML, true)
        resultService
          .exportResultById(resultItems, rootState.authentication.profile.laboratoryTypeOutputResult)
          .then(response => {
            analyticsOnExportingXml(resultItems, 'Exportar Resultados')
            resolve(createFileDownloadLinkObj(response))
            notify({
              title: 'AVISO:',
              message: 'Exportação efetuada com sucesso.'
            })
            commit(RESULT_SENDING_XML, false)
          })
          .catch(() => {
            resolve(null)
            notify({
              title: 'AVISO:',
              message: 'Erro ao exportar os resultados. Por favor tente novamente.'
            })
            commit(RESULT_SENDING_XML, false)
          })
      } else {
        notify({
          title: 'AVISO:',
          message: 'Nenhum resultado selecionado.'
        })
        resolve(null)
      }
    })
  },
  sortResultList: function ({ commit }, param) {
    commit(RESULT_SORT_LIST, param)
  },
  getTotalResults: function ({ commit, dispatch, state }) {
    resultService
      .getTotalResults(
        strings.listsLimits.results,
        state.samplePrintedList,
        state.timestamp,
        state.exported,
        state.q,
        state.integrated
      )
      .then(response => {
        commit(RESULT_TOTAL_COUNT, response.data.batchesCount)
      })
  },
  breakResultsLoading: function ({ commit }) {
    commit(RESULT_SET_LOADING_STATUS, 'STATUS_FINAL')
    commit(RESULT_CLEAR_ALL)
    commit(RESULT_BREAK_STATUS, true)
    window.stop()
  },
  exportResultByFilter({ commit, state, rootState }) {
    return new Promise((resolve, reject) => {
      commit(RESULT_BEFORE_LIST_SENDING_XML, true)
      resultService
        .exportResultByFilter(
          state.timestamp,
          state.exported,
          state.dateType,
          state.integrated,
          rootState.authentication.profile.laboratoryTypeOutputResult
        )
        .then(response => {
          if (response.status === 200) {
            resolve(createFileDownloadLinkObj(response))
            notify({
              title: 'AVISO:',
              message: 'Exportação efetuada com sucesso.'
            })
            commit(RESULT_BEFORE_LIST_SENDING_XML, false)
            analyticsOnFetchingFromSearch(state)
          } else if (response.status === 204) {
            resolve(null)
            notify({
              title: 'AVISO:',
              message: 'Não foram encontrados resultados disponíveis para essa busca.'
            })
            commit(RESULT_BEFORE_LIST_SENDING_XML, false)
          }
        })
        .catch(() => {
          resolve(null)
          notify({
            title: 'AVISO:',
            message: 'Erro ao exportar os resultados. Por favor tente novamente.'
          })
          commit(RESULT_BEFORE_LIST_SENDING_XML, false)
        })
    })
  },
  updateResultFilters({ commit }, filter) {
    commit(RESULT_FILTERS, filter)
  },
  updateResultsStatistics({ commit, dispatch }, days) {
    dispatch('selectResultList', {
      startDate: Moment(new Date()).subtract(days, 'days').toDate(),
      endDate: Moment(new Date()).toDate(),
      exported: false,
      dateType: 'RD',
      integrated: null
    })
    resultService
      .getResultsStatistics(
        state.offset,
        strings.listsLimits.results,
        state.timestamp.start,
        state.timestamp.end,
        state.exported,
        state.integrated
      )
      .then(response => {
        commit(RESULT_STATISTICS_LOADING_STATUS, 'STATUS_SUCCESS')
        commit(RESULT_STATISTICS, response.data)
      })
      .catch(() => {
        commit(RESULT_STATISTICS_LOADING_STATUS, 'STATUS_SUCCESS')
      })
  },
  resetResultsQuery({ commit }) {
    commit(RESULT_SET_QUERY)
  },
  resetResultsStatistics({ commit }) {
    let statistics = {
      resultsNewCount: 0,
      resultsOldCount: 0,
      resultsHasManual: false,
      resultsHasIntegrated: false
    }
    commit(RESULT_STATISTICS, statistics)
    commit(RESULT_STATISTICS_LOADING_STATUS, 'STATUS_INITIAL')
  },
  openResultDetails({ commit }, patientName) {
    commit(RESULT_EXPAND_DETAILS, patientName)
  },
  setResultSelected({ commit }, value) {
    commit(RESULT_SET_SELECTED, value)
  },
  showOffResultDetails({ commit }, val) {
    commit(RESULT_SHOW_OFF_ALL_DETAILS, val)
  }
}

let createFileDownloadLinkObj = function (response) {
  let blob = new Blob([response.data], { type: 'application/xml;charset=ISO-8859-1', encoding: 'ISO-8859-1' })
  let date = new Date()
  let filename = 'resultados_' + date.toISOString() + '.xml'
  let result = document.createElement('a')
  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    result.href = window.URL.createObjectURL(blob)
    result.download = filename
  }
  result.style.visibility = 'hidden'
  return result
}

let analyticsOnExportingXml = function (selectedItems, origin) {
  let mostRecentDate
  let olderDate
  let numExported = 0
  let numNotExported = 0
  let numFixed = 0

  state.results.forEach(function (result) {
    if (result.exported) {
      numExported++
    } else {
      numNotExported++
    }

    if (result.amendment) {
      numFixed++
    }
  })

  selectedItems.forEach(function (result) {
    let index = state.results
      .map(function (o) {
        return o.id
      })
      .indexOf(result)

    if (!mostRecentDate) {
      mostRecentDate = state.results[index].dateResult
    } else {
      if (mostRecentDate > state.results[index].dateResult) {
        mostRecentDate = state.results[index].dateResult
      }
    }

    if (!olderDate) {
      olderDate = state.results[index].dateResult
    } else {
      if (olderDate < state.results[index].dateResult) {
        olderDate = state.results[index].dateResult
      }
    }

    analytics.pushAnalyticsEvent('ga-event', {
      category: origin,
      action: mostRecentDate,
      label: olderDate,
      'status-n': numNotExported,
      'status-s': numExported,
      'status-r': numFixed
    })
  })
}

let analyticsOnFetchingFromSearch = function (state) {
  analytics.pushAnalyticsEvent('ga-search', {
    exported: state.exported,
    startDate: state.timestamp.start,
    endDate: state.timestamp.end,
    codeOsLis: state.codeOsLis,
    codeOsAlvaro: state.codeOsAlvaro,
    patientName: state.patientName,
    dateType: state.dateType,
    integrated: state.integrated
  })
}

export default { state, mutations, actions, getters }
export { mutations, actions, getters }
